'use client';

import { Edit, GripVertical, Plus, Trash2 } from 'lucide-react';
import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Button } from '../../ui/button';
import type { FormFieldSchema } from '../types';

interface BuilderAreaProps {
  fields: FormFieldSchema[];
  onEditField: (fieldId: string) => void;
  onDeleteField: (fieldId: string) => void;
  onReorderFields: (fromIndex: number, toIndex: number) => void;
  onAddField?: (fieldType: FormFieldSchema['type']) => void;
}

interface DraggableFieldProps {
  field: FormFieldSchema;
  index: number;
  onEditField: (fieldId: string) => void;
  onDeleteField: (fieldId: string) => void;
  onReorderFields: (fromIndex: number, toIndex: number) => void;
}

function DraggableField({
  field,
  index,
  onEditField,
  onDeleteField,
  onReorderFields,
}: DraggableFieldProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'EXISTING_FIELD',
      item: { index },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [index]
  );

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: ['EXISTING_FIELD'],
      drop: (item: unknown) => {
        const dropItem = item as { index?: number };
        if (dropItem.index !== undefined && dropItem.index !== index) {
          onReorderFields(dropItem.index, index);
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [index, onReorderFields]
  );

  // react-dnd's connector functions register the DOM node with the drag/drop backend; they
  // don't dereference ref.current synchronously, so this is safe despite the compiler's
  // conservative static check. Wiring this in an effect instead would connect it a frame late,
  // causing a flash of non-draggable content on mount.
  // eslint-disable-next-line react-hooks/refs
  drag(drop(ref));

  const fieldAny = field as unknown as Record<string, unknown>;

  return (
    <div
      ref={ref}
      className={`p-4 bg-card border-2 rounded-lg transition-all cursor-move group hover:shadow-md ${
        isDragging ? 'opacity-40 scale-95' : ''
      } ${isOver ? 'border-primary bg-primary/5' : 'border-border hover:border-border-hover'}`}
    >
      <div className="flex items-center gap-3">
        <GripVertical className="w-5 h-5 text-slate-300 opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity" />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{field.label}</p>
          <p className="text-xs text-muted-foreground mt-1">
            <span className="inline-block px-2 py-0.5 bg-muted rounded text-muted-foreground mr-2">
              {field.type}
            </span>
            {field.required && (
              <span className="inline-block px-2 py-0.5 bg-destructive/10 text-destructive rounded text-xs font-medium mr-2">
                Required
              </span>
            )}
            {!!fieldAny.multiple && (
              <span className="inline-block px-2 py-0.5 bg-blue-500/10 text-blue-600 rounded text-xs font-medium mr-2">
                Multiple
              </span>
            )}
            {fieldAny.minLength !== undefined && (
              <span className="inline-block px-2 py-0.5 bg-muted rounded text-muted-foreground mr-2 text-[10px]">
                Min: {fieldAny.minLength as number}
              </span>
            )}
            {fieldAny.maxLength !== undefined && (
              <span className="inline-block px-2 py-0.5 bg-muted rounded text-muted-foreground mr-2 text-[10px]">
                Max: {fieldAny.maxLength as number}
              </span>
            )}
            {fieldAny.minRows !== undefined && (
              <span className="inline-block px-2 py-0.5 bg-muted rounded text-muted-foreground mr-2 text-[10px]">
                Rows: {fieldAny.minRows as number}
              </span>
            )}
          </p>
        </div>

        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEditField(field.id)}
            className="p-2 h-auto hover:bg-primary/10"
          >
            <Edit className="w-4 h-4 text-primary" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDeleteField(field.id)}
            className="p-2 h-auto hover:bg-destructive/10"
          >
            <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function BuilderArea({
  fields,
  onEditField,
  onDeleteField,
  onReorderFields,
  onAddField,
}: BuilderAreaProps) {
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: ['FIELD_TYPE'],
      drop: (item: unknown) => {
        const dropItem = item as { fieldType?: FormFieldSchema['type'] };
        if (dropItem.fieldType && onAddField) {
          onAddField(dropItem.fieldType);
          return { dropped: true };
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [onAddField]
  );

  const ref = useRef<HTMLDivElement>(null);
  // See the justification on the DraggableField `drag(drop(ref))` call above.
  // eslint-disable-next-line react-hooks/refs
  drop(ref);

  return (
    <div ref={ref} className="p-6 flex flex-col h-full">
      <h2 className="text-lg font-bold mb-2">Form Builder</h2>
      <p className="text-xs text-muted-foreground mb-6">Create your custom form here</p>

      {fields.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-center pointer-events-none">
          <div>
            <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-primary/60" />
            </div>
            <p className="text-muted-foreground text-sm font-medium">No fields yet</p>
            <p className="text-muted-foreground/60 text-xs mt-1">
              Drag field types from the left panel
            </p>
          </div>
        </div>
      ) : (
        <div className={`space-y-3 flex-1 ${isOver ? 'bg-primary/5 rounded-lg' : ''}`}>
          {fields.map((field, index) => (
            <DraggableField
              key={field.id}
              field={field}
              index={index}
              onEditField={onEditField}
              onDeleteField={onDeleteField}
              onReorderFields={onReorderFields}
            />
          ))}
        </div>
      )}
    </div>
  );
}
